import { zodResolver } from '@hookform/resolvers/zod';
import { clsx } from 'clsx';
import { useEffect, useState } from 'react';
import { useForm, useFormContext } from 'react-hook-form';
import { z } from 'zod';

import { Button } from '@repo/ui/Button';
import { Flex } from '@repo/ui/Flex';
import { Icon } from '@repo/ui/Icon';
import { Input } from '@repo/ui/Input';
import { InputField } from '@repo/ui/Input/InputField';
import { InputGroup } from '@repo/ui/Input/InputGroup';
import { InputLabel } from '@repo/ui/Input/InputLabel';
import { InputMessage } from '@repo/ui/Input/InputMessage';
import { InputRoot } from '@repo/ui/Input/InputRoot';
import { rawString, Text } from '../Language/Language';
import { useWorkflow } from '../../workflow/hooks';

import s from './SearchBox.module.css';
import { pdxEvent } from '../../utils/noflake';

export const PostalCodeSearchSchema = z.object({
  postalCodeInternal: z.string().min(4, {
    message: rawString({
      langKey: 'Global.delivery.locationPicker.searchBox.error',
    }),
  }),
});

export type PostalCodeSearchSchemaType = z.infer<typeof PostalCodeSearchSchema>;

export const SearchBox = ({
  setPostalCode,
}: {
  setPostalCode: (postalCode: string) => void;
}) => {
  const [showSearch, setShowSearch] = useState(false);
  const { watch, setValue } = useFormContext();
  const postalCode = watch('locations.postalCode');
  const { order } = useWorkflow();
  const orderPostalCode = order?.shippingAddress?.postalCode;
  const internalMethods = useForm<PostalCodeSearchSchemaType>({
    resolver: zodResolver(PostalCodeSearchSchema),
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    defaultValues: {
      postalCodeInternal: '',
    },
  });
  const {
    formState: searchFormState,
    handleSubmit: searchHandleSubmit,
    register: searchRegister,
  } = internalMethods;
  const { errors, isValid } = searchFormState;

  useEffect(() => {
    if (!postalCode) {
      setValue('locations.postalCode', orderPostalCode);
    }
    // eslint-disable-next-line exhaustive-deps
  }, []);

  const handleSubmit = searchHandleSubmit((data, e) => {
    e?.preventDefault();
    pdxEvent({
      event_category: 'set_data',
      event_type: 'set_postal_code',
      event_value: data.postalCodeInternal.trim(),
    });
    setPostalCode(data.postalCodeInternal.trim() ?? '');
  });

  return (
    <Flex
      padding="p-16"
      className={s.wrapper}
      direction="column"
      gap="g-12"
      alignItems="center"
    >
      <Flex direction="row" justifyContent="center" alignItems="center">
        <Text langKey="Global.delivery.locationPicker.searchBox.title" />
        <Button
          onClick={(e) => {
            e.preventDefault();
            setShowSearch(!showSearch);
          }}
          variant="link"
        >
          <strong>{postalCode}</strong>
          <Icon
            className={clsx(s.chevron, {
              [s.chevronDown]: !showSearch,
            })}
            name="chevron"
            size="sm"
          />
        </Button>
      </Flex>
      {showSearch && (
        <Flex>
          <InputRoot width="full">
            <InputGroup className={s.inputGroup}>
              <InputField>
                <InputLabel
                  htmlFor="postalCodeInternal"
                  variant={errors.postalCodeInternal ? 'error' : 'default'}
                >
                  {rawString({
                    langKey: 'Global.delivery.locationPicker.searchBox.label',
                  })}
                </InputLabel>
                <Input
                  className={s.input}
                  id="postalCodeInternal"
                  {...searchRegister('postalCodeInternal')}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleSubmit(e);
                    }
                  }}
                />
              </InputField>
              <Button ariaDisabled={!isValid} onClick={handleSubmit}>
                <Text langKey="Global.delivery.locationPicker.searchBox.button" />
              </Button>
            </InputGroup>
            {errors.postalCodeInternal && (
              <InputMessage variant="error">
                <Icon name="alert" size="font" />
                {errors.postalCodeInternal.message}
              </InputMessage>
            )}
          </InputRoot>
        </Flex>
      )}
    </Flex>
  );
};
